import React, { useState, useEffect } from 'react';
import { fetchUserList, fetchComprehensiveUserData, maskUserId } from '../utils/adminApi';

interface UserData {
  userId: string;
  maskedUserId: string;
  livePosition: string;
  weeklyPnL: number;
  profitShare: number;
  hyperFunds: number;
  escrowBalance: number;
  totalTrades: number;
  isActive: boolean;
}

interface SummaryData {
  totalWeeklyProfit: number;
  totalWeeklyUsers: number;
  totalProfitShare: number;
  totalTrades: number;
}

const SummaryCards: React.FC<{ data: SummaryData }> = ({ data }) => (
  <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
    <div style={{ flex: 1, padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <h3 style={{ color: 'black', margin: '0 0 10px 0' }}>Weekly Profit</h3>
      <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'green', margin: 0 }}>
        ${data.totalWeeklyProfit.toFixed(2)}
      </p>

    </div>
    <div style={{ flex: 1, padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <h3 style={{ color: 'black', margin: '0 0 10px 0' }}>Weekly Users</h3>
      <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'black', margin: 0 }}>{data.totalWeeklyUsers}</p>

    </div>
    <div style={{ flex: 1, padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <h3 style={{ color: 'black', margin: '0 0 10px 0' }}>Weekly P&L Share</h3>
      <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'green', margin: 0 }}>
        ${data.totalProfitShare.toFixed(2)}
      </p>

    </div>
    <div style={{ flex: 1, padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <h3 style={{ color: 'black', margin: '0 0 10px 0' }}>Total Trades</h3>
      <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'blue', margin: 0 }}>
        {data.totalTrades}
      </p>

    </div>
  </div>
);

const FDBPanel: React.FC<{ data: any }> = ({ data }) => {
  if (!data) return <div style={{padding:"20px",color:"white"}}>FDB Loading...</div>;
  const b = data.band_info || {};
  const l = data.log_data || {};
  const pass = (v: boolean) => ({ color: v ? "lime" : "red", fontWeight: "bold" as const });
  const rows = [
    { label: "outlier_detected", val: String(b.outlier_detected), ok: b.outlier_detected === false },
    { label: "outlier_scale_factor", val: String(b.outlier_scale_factor), ok: b.outlier_scale_factor === 1.0 },
    { label: "avg_candle_body", val: String(b.avg_candle_body?.toFixed(4)), ok: true },
    { label: "candle_body_std", val: String(b.candle_body_std?.toFixed(4)), ok: true },
    { label: "bias", val: l.bias, ok: l.bias === "long_only" || l.bias === "short_only" },
    { label: "all_bands_rising", val: l.bands_rising, ok: l.bands_rising === "True" },
    { label: "all_bands_falling", val: l.bands_falling, ok: l.bands_falling === "True" },
    { label: "band_widening", val: l.band_widening, ok: l.band_widening === "True" },
    { label: "candle_slope", val: l.candle_slope, ok: true },
    { label: "chop", val: l.chop, ok: parseFloat(l.chop) < 52.5 },
    { label: "slope", val: l.slope, ok: true },
    { label: "pct_from_peak", val: l.pct_from_peak, ok: parseFloat(l.pct_from_peak) < 0.35 },
    { label: "pct_from_trough", val: l.pct_from_trough, ok: parseFloat(l.pct_from_trough) < 0.35 },
    { label: "rolling_peak", val: String(b.rolling_peak), ok: true },
    { label: "rolling_trough", val: String(b.rolling_trough), ok: true },
  ];
  return (
    <div style={{marginTop:"30px",padding:"20px",border:"1px solid #444",borderRadius:"8px",backgroundColor:"#1a1a1a"}}>
      <h3 style={{color:"white",marginBottom:"15px"}}>📊 FDB Entry Conditions</h3>
      <div style={{display:"grid",gridTemplateColumns:"200px 150px 80px",gap:"8px",color:"white"}}>
        <div style={{fontWeight:"bold"}}>Condition</div><div style={{fontWeight:"bold"}}>Value</div><div style={{fontWeight:"bold"}}>Pass</div>
        {rows.map(r => (<React.Fragment key={r.label}><div>{r.label}</div><div>{r.val}</div><div style={pass(r.ok)}>{r.ok ? "✅" : "❌"}</div></React.Fragment>))}
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const storeCredentials = async () => {
    const walletElement = document.getElementById("walletAddress") as HTMLInputElement;
    const privateKeyElement = document.getElementById("privateKey") as HTMLInputElement;
    const networkElement = document.getElementById("network") as HTMLSelectElement;
    
    if (!walletElement?.value || !privateKeyElement?.value) {
      alert("Please fill in both wallet address and private key");
      return;
    }
    
    try {
      const response = await fetch("/api/admin/store-deployment-credentials", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          wallet_address: walletElement.value,
          private_key: privateKeyElement.value,
          network: networkElement.value
        })
      });
      
      const result = await response.json();
      alert(result.success ? "Credentials stored successfully for 10 years" : "Failed to store credentials");
      
      if (result.success) {
        walletElement.value = "";
        privateKeyElement.value = "";
      }
    } catch (error) {
      alert("Error connecting to smart contract API");
    }
  };

  const deployToTestnet = async () => {
    setDeploymentStatus("Deploying to testnet...");
    try {
      const response = await fetch("/api/admin/deploy-contract?network=testnet", {
        method: "POST"
      });
      const result = await response.json();
      setDeploymentStatus(result.message || "Deployment complete");
      if (result.contract_address) setContractAddress(result.contract_address);
    } catch (error) {
      setDeploymentStatus("Deployment failed");
    }
  };

  const deployToMainnet = async () => {
    setDeploymentStatus("Deploying to mainnet...");
    try {
      const response = await fetch("/api/admin/deploy-contract?network=mainnet", {
        method: "POST"
      });
      const result = await response.json();
      setDeploymentStatus(result.message || "Deployment complete");
      if (result.contract_address) setContractAddress(result.contract_address);
    } catch (error) {
      setDeploymentStatus("Deployment failed");
    }
  };
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [deploymentStatus, setDeploymentStatus] = useState("");
  const [contractAddress, setContractAddress] = useState("");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [fdbData, setFdbData] = useState<any>(null);

  const aggregateSummary = (): SummaryData => ({
    totalWeeklyProfit: users.reduce((sum, user) => sum + user.weeklyPnL, 0),
    totalWeeklyUsers: users.length,
    totalProfitShare: users.reduce((sum, user) => sum + user.profitShare, 0),
    totalTrades: users.reduce((sum, user) => sum + user.totalTrades, 0)
  });


  // Fetch actual escrow balance from contract
  const fetchEscrowBalance = async (userId: string): Promise<number> => {
    try {
      const walletResponse = await fetch(`/api/admin/user-wallet-proxy/${encodeURIComponent(userId)}`);
      if (!walletResponse.ok) return 0;
      
      const walletData = await walletResponse.json();
      if (!walletData.success || !walletData.wallet_address) return 0;
      
      const escrowAddress = "0xdF7c107a6d33E73e85b04f97A82634497C1E2Add";
      const url = `/api/escrow/balance?escrow_address=${escrowAddress}&user_address=${walletData.wallet_address}&network=mainnet`;
      
      const response = await fetch(url);
      if (!response.ok) return 0;
      
      const data = await response.json();
      return data.success ? (data.balance_usdc || 0) : 0;
    } catch (error) {
      console.error(`Failed to fetch escrow for ${userId}:`, error);
      return 0;
    }
  };

  const fetchAllUserData = async () => {
    setLoading(true);
    const userIds = await fetchUserList();
    const userData: UserData[] = [];

    for (const userId of userIds) {
      const data = await fetchComprehensiveUserData(userId);
      
      if (data && data.weekly && data.weekly.success) {
        const escrowBal = await fetchEscrowBalance(userId);
        userData.push({
          userId, maskedUserId: maskUserId(userId),
          livePosition: data.status?.status === 'running' ? 'ACTIVE' : 'INACTIVE',
          weeklyPnL: data.weekly.data.total_pnl || 0,
          profitShare: data.weekly.data.profit_share || 0,
          hyperFunds: data.balance?.usdc_balance || 0,
          escrowBalance: escrowBal,
          totalTrades: data.weekly.data.trade_count || 0,
          isActive: data.status?.active || false });
      }
    }

    setUsers(userData);
    setLoading(false);
    setLastUpdate(new Date());
  };

  const toggleUser = (userId: string) => {
    setUsers(users.map(user => 
      user.userId === userId 
        ? { ...user, isActive: !user.isActive }
        : user
    ));
  };

  const fetchFdbData = async () => {
    try {
      const response = await fetch("/api/fdb/status");
      const data = await response.json();
      setFdbData(data);
    } catch (error) {
      console.error("FDB fetch failed:", error);
    }
  };

  const fetchDeployedContract = async () => {
    try {
      const response = await fetch("/api/admin/deployment-status");
      const result = await response.json();
      
      // Check mainnet deployment first (priority)
      if (result.deployments?.mainnet?.deployed) {
        setContractAddress(result.deployments.mainnet.escrow_address);
        setDeploymentStatus(`✅ ${result.status}`);
      } 
      // Fall back to testnet if mainnet not deployed
      else if (result.deployments?.testnet?.deployed) {
        setContractAddress(result.deployments.testnet.escrow_address);
        setDeploymentStatus(`🧪 Deployed to Testnet`);
      }
      // No deployments
      else {
        setDeploymentStatus(result.status || "Not Deployed");
      }
    } catch (error) {
      console.error("Failed to fetch deployment status:", error);
      setDeploymentStatus("Error fetching status");
    }
  };
  useEffect(() => {
    fetchAllUserData();
    fetchDeployedContract();
    const interval = setInterval(fetchAllUserData, 120000);
    fetchFdbData();
    const fdbInterval = setInterval(fetchFdbData, 5000);
    return () => { clearInterval(interval); clearInterval(fdbInterval); };
  }, []);

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Admin Dashboard</h1>
        <button onClick={fetchAllUserData} style={{ padding: '10px 20px' }}>Refresh</button>
      </div>
      
      <SummaryCards data={aggregateSummary()} />
      
      <div style={{ marginTop: '30px' }}>
        <h2 style={{ marginBottom: '10px' }}>Users ({users.length})</h2>
        
        {/* Table Headers */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '120px 120px 100px 120px 100px 100px 100px 80px', 
          gap: '15px', 
          padding: '10px',
          fontWeight: 'bold',
          color: 'white',
          marginBottom: '5px'
        }}>
          <div>User ID</div>
          <div>Live Position</div>
          <div>Weekly P&L</div>
          <div>Profit Share</div>
          <div>Hyper Funds</div>
          <div>Escrow Balance</div>
          <div>Total Trades</div>
          <div>Status</div>
        </div>
        
        {/* User Rows */}
        {users.map(user => (
          <div key={user.userId} style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px 120px 100px 120px 100px 100px 100px 80px', 
            gap: '15px',
            padding: '15px',
            margin: '5px 0',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #ddd',
            color: 'black'
          }}>
            <div>{user.maskedUserId}</div>
            <div style={{ color: user.livePosition === 'ACTIVE' ? 'green' : 'red' }}>
              {user.livePosition}
            </div>
            <div style={{ color: user.weeklyPnL >= 0 ? 'green' : 'red' }}>
              ${user.weeklyPnL.toFixed(2)}
            </div>
            <div style={{ color: 'green' }}>
              ${user.profitShare.toFixed(2)}
            </div>
            <div>${user.hyperFunds.toFixed(2)}</div>
            <div>${user.escrowBalance.toFixed(2)}</div>
            <div>{user.totalTrades}</div>
            <div>
              <button 
                onClick={() => toggleUser(user.userId)}
                style={{ 
                  backgroundColor: user.isActive ? 'green' : 'gray',
                  color: 'white',
                  border: 'none',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {user.isActive ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
        ))}
      </div>


      <FDBPanel data={fdbData} />
      <div className="admin-section" style={{marginTop: "30px", padding: "20px", border: "1px solid #ddd", borderRadius: "8px"}}>
        <h3>🔒 Smart Contract Management</h3>
        <div>Status: {deploymentStatus || "Checking..."}</div>
        <br/>
        <div>
          <h4>Admin Deployment Credentials</h4>
          <input type="text" placeholder="Wallet Address" id="walletAddress" style={{width: "300px", padding: "5px", margin: "5px"}} /><br/>
          <input type="password" placeholder="Private Key" id="privateKey" style={{width: "300px", padding: "5px", margin: "5px"}} /><br/>
          <select id="network" style={{padding: "5px", margin: "5px"}}>
            <option value="testnet">Testnet</option>
            <option value="mainnet">Mainnet</option>
          </select><br/>
          <button onClick={storeCredentials} style={{padding: "10px", margin: "5px", backgroundColor: "green", color: "white"}}>Store Credentials</button>
        </div>
        <br/>
        <div>
          <h4>Deployment Status</h4>
          <div style={{padding: "10px", backgroundColor: "#f0f0f0", borderRadius: "4px", marginBottom: "10px"}}>
            <div style={{color: "#000"}}>Status: {deploymentStatus || "Ready"}</div>
            {contractAddress && <div style={{color: "#000"}}>Contract: {contractAddress}</div>}
          </div>
          <button onClick={deployToTestnet} style={{padding: "10px 20px", margin: "5px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px"}}>🧪 Deploy to Testnet</button>
          <button onClick={deployToMainnet} style={{padding: "10px 20px", margin: "5px", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px"}}>🚀 Deploy to Mainnet</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
